import { useState, useEffect, useRef } from 'react';
import useEsMovil from '../hooks/useEsMovil';
import { SidebarIcons } from '../constants';
// Íconos de línea con nombre en mayúscula (react/jsx-pascal-case).
const IcCalendario = SidebarIcons.calendario;

// ═══════════════════════════════════════════════════════════════════════════════
// Campo de fecha en formato de Guatemala: dd/mm/aaaa (2026-09-28)
//
// El <input type="date"> nativo NO se puede formatear: el navegador lo pinta
// según el idioma del sistema operativo, y en una Mac en inglés sale
// mm/dd/yyyy. Este componente lo reemplaza sin cambiar nada hacia adentro:
//
//   · Se escribe (o se ve) dd/mm/aaaa. Las barras se ponen solas.
//   · El botón 📅 abre el calendario del navegador para elegir con el mouse.
//   · Hacia el código entra y sale el MISMO valor de siempre: 'AAAA-MM-DD'
//     (o '' si está vacío), y onChange recibe un evento con e.target.value.
//     Por eso se cambia `<input type="date" …>` por `<InputFecha …>` y ya.
//
// Mientras la fecha está a medio escribir no se avisa nada hacia afuera: el
// valor anterior se conserva hasta que la fecha está completa y es válida.
// Si se sale del campo con una fecha incompleta o imposible (31/02), vuelve a
// mostrar la última fecha válida.
// ═══════════════════════════════════════════════════════════════════════════════

const p2 = (n) => String(n).padStart(2, '0');

// 'AAAA-MM-DD' (o ISO completo) → 'dd/mm/aaaa'
export function isoADdmm(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
}

// 'dd/mm/aaaa' (acepta 1 dígito en día/mes y separadores / - .) → 'AAAA-MM-DD' | null
export function ddmmAIso(txt) {
  const t = String(txt || '').trim();
  let m = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(t);
  if (!m) m = /^(\d{2})(\d{2})(\d{4})$/.exec(t);
  if (!m) return null;
  const d = Number(m[1]), mes = Number(m[2]), a = Number(m[3]);
  if (a < 1900 || a > 2100 || mes < 1 || mes > 12 || d < 1) return null;
  const dim = new Date(Date.UTC(a, mes, 0)).getUTCDate();
  if (d > dim) return null;
  return `${a}-${p2(mes)}-${p2(d)}`;
}

// Pone las barras mientras se escribe: "280920" → "28/09/20"
function enmascarar(raw, previo) {
  let t = String(raw || '').replace(/[.-]/g, '/').replace(/[^\d/]/g, '').slice(0, 10);
  if (t.length < String(previo || '').length) return t;          // está borrando
  if (/^\d{2}$/.test(t) || /^\d{1,2}\/\d{2}$/.test(t)) t += '/';
  if (/^\d{3}$/.test(t)) t = `${t.slice(0, 2)}/${t.slice(2)}`;
  if (/^\d{1,2}\/\d{3}$/.test(t)) t = t.replace(/^(\d{1,2}\/\d{2})(\d)$/, '$1/$2');
  return t.replace(/\/{2,}/g, '/');
}

export default function InputFecha({
  value, onChange, onBlur, style, className, min, max, disabled, readOnly,
  title, id, name, autoFocus, required, placeholder = 'dd/mm/aaaa',
}) {
  const esMovil = useEsMovil();
  const iso = value ? String(value).slice(0, 10) : '';
  const [txt, setTxt] = useState(isoADdmm(iso));
  const nativo = useRef(null);
  const texto  = useRef(null);

  // Si el valor cambia desde afuera (otro paciente, "Hoy", limpiar…), se refleja.
  useEffect(() => {
    setTxt((t) => (ddmmAIso(t) === iso && iso ? t : isoADdmm(iso)));
  }, [iso]);

  const emitir = (v) => {
    if (!onChange) return;
    const target = { value: v, name, id };
    onChange({ target, currentTarget: target, preventDefault() {}, stopPropagation() {} });
  };

  const alEscribir = (e) => {
    const t = enmascarar(e.target.value, txt);
    setTxt(t);
    if (!t.trim()) { if (iso) emitir(''); return; }
    const nuevo = ddmmAIso(t);
    if (nuevo && nuevo !== iso) emitir(nuevo);
  };

  const alSalir = (e) => {
    const nuevo = ddmmAIso(txt);
    if (txt.trim() && !nuevo) setTxt(isoADdmm(iso));
    else if (nuevo) setTxt(isoADdmm(nuevo));
    if (onBlur) onBlur(e);
  };

  const abrirCalendario = () => {
    const el = nativo.current;
    if (!el || disabled || readOnly) return;
    try { if (typeof el.showPicker === 'function') { el.showPicker(); return; } } catch { /* sigue */ }
    el.focus(); el.click();
  };

  const s = style || {};
  const envoltura = {
    position: 'relative',
    display: s.display === 'none' ? 'none' : (s.width && s.width !== '100%' ? 'inline-block' : (className && !s.width ? 'inline-block' : 'block')),
    width: s.width, maxWidth: s.maxWidth, minWidth: s.minWidth,
    marginTop: s.marginTop, marginBottom: s.marginBottom, flex: s.flex,
  };
  const campo = {
    ...s, width: '100%', maxWidth: 'none', minWidth: 0, marginTop: 0, marginBottom: 0, flex: undefined,
    boxSizing: 'border-box', paddingRight: 32,
  };

  return (
    <span style={envoltura}>
      <input
        ref={texto} type="text" inputMode="numeric" autoComplete="off" maxLength={10}
        id={id} name={name} className={className} style={campo} title={title}
        placeholder={placeholder} value={txt} disabled={disabled} readOnly={readOnly}
        autoFocus={autoFocus} required={required}
        onChange={alEscribir} onBlur={alSalir}
      />
      <button
        type="button" tabIndex={-1} onClick={abrirCalendario} disabled={disabled || readOnly}
        title="Elegir en el calendario"
        style={{
          position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)',
          background: 'none', border: 'none', padding: '2px 5px', lineHeight: 1,
          fontSize: 14, cursor: disabled || readOnly ? 'default' : 'pointer', opacity: disabled ? 0.4 : 0.75,
        }}
      >{esMovil
        // Teléfono: ícono de línea (sin emojis en la interfaz); el botón ya mide 44 px por movil.css.
        ? <IcCalendario size={20} style={{ color: '#4A8CF0', display: 'block' }} />
        : '📅'}</button>
      {/* Solo para el calendario del navegador; no se ve ni recibe foco. */}
      <input
        ref={nativo} type="date" tabIndex={-1} aria-hidden="true"
        value={iso} min={min} max={max}
        onChange={(e) => {
          const v = e.target.value || '';
          setTxt(isoADdmm(v)); emitir(v);
          // Se devuelve el foco al campo: los que guardan "al salir" (onBlur)
          // guardan cuando el usuario sigue a otra cosa, igual que con el nativo.
          texto.current?.focus();
        }}
        style={{ position: 'absolute', right: 0, bottom: 0, width: 1, height: 1, opacity: 0, pointerEvents: 'none', border: 0, padding: 0 }}
      />
    </span>
  );
}

// Fecha + hora juntos, para los campos que antes eran datetime-local.
// Entra y sale 'AAAA-MM-DDTHH:mm', igual que el nativo.
export function InputFechaHora({ value, onChange, style, disabled }) {
  const v = String(value || '');
  const fecha = v.slice(0, 10);
  // La hora se recuerda aparte: si se borra la fecha para reescribirla, no se pierde.
  const [hora, setHora] = useState(v.length >= 16 ? v.slice(11, 16) : '');
  useEffect(() => { if (v.length >= 16) setHora(v.slice(11, 16)); }, [v]);
  const emitir = (f, h) => {
    setHora(h || '');
    const val = f ? `${f}T${h || '00:00'}` : '';
    const target = { value: val };
    onChange?.({ target, currentTarget: target, preventDefault() {}, stopPropagation() {} });
  };
  const s = style || {};
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      <div style={{ flex: 1.4, minWidth: 0 }}>
        <InputFecha style={{ ...s, width: '100%' }} value={fecha} disabled={disabled}
          onChange={(e) => emitir(e.target.value, hora)} />
      </div>
      <input type="time" style={{ ...s, flex: 1, width: 'auto', minWidth: 0 }} value={hora} disabled={disabled}
        onChange={(e) => emitir(fecha, e.target.value)} />
    </div>
  );
}
